/**
 * MUI localisation bound to the interface language.
 *
 * MUI components carry their own built-in texts (pagination, autocomplete,
 * date pickers...) and stay in English unless they are given a locale. The
 * date pickers also need a date-fns locale for month and day names, the first
 * day of the week and the input format.
 */

import { arSD, enUS, frFR } from '@mui/material/locale';
import { enUS as pickersEnUS, frFR as pickersFrFR } from '@mui/x-date-pickers/locales';
import { ar, enUS as dateFnsEnUS, fr } from 'date-fns/locale';

import type { LanguageCode } from './index';
import type { Localization } from '@mui/material/locale';
import type { PickersLocaleText } from '@mui/x-date-pickers/locales';
import type { TimeViewWithMeridiem } from '@mui/x-date-pickers/internals';
import type { Locale } from 'date-fns';

type PickersText = Partial<PickersLocaleText<Date>>;

const AR_VIEWS: Record<TimeViewWithMeridiem, string> = {
  hours: 'الساعات',
  minutes: 'الدقائق',
  seconds: 'الثواني',
  meridiem: 'صباحًا/مساءً',
};

// MUI X ships no Arabic translation of the date pickers.
const arPickersText: PickersText = {
  previousMonth: 'الشهر السابق',
  nextMonth: 'الشهر التالي',
  openPreviousView: 'فتح العرض السابق',
  openNextView: 'فتح العرض التالي',
  calendarViewSwitchingButtonAriaLabel: (view) =>
    view === 'year'
      ? 'عرض السنوات مفتوح، الانتقال إلى عرض التقويم'
      : 'عرض التقويم مفتوح، الانتقال إلى عرض السنوات',
  start: 'البداية',
  end: 'النهاية',
  startDate: 'تاريخ البداية',
  startTime: 'وقت البداية',
  endDate: 'تاريخ النهاية',
  endTime: 'وقت النهاية',
  cancelButtonLabel: 'إلغاء',
  clearButtonLabel: 'مسح',
  okButtonLabel: 'موافق',
  todayButtonLabel: 'اليوم',
  datePickerToolbarTitle: 'اختر التاريخ',
  dateTimePickerToolbarTitle: 'اختر التاريخ والوقت',
  timePickerToolbarTitle: 'اختر الوقت',
  dateRangePickerToolbarTitle: 'اختر الفترة',
  clockLabelText: (view, time, utils, formattedTime) =>
    `اختر ${AR_VIEWS[view]}. ${
      !formattedTime && (time === null || !utils.isValid(time))
        ? 'لم يتم اختيار أي وقت'
        : `الوقت المحدد هو ${formattedTime ?? utils.format(time as Date, 'fullTime')}`
    }`,
  hoursClockNumberText: (hours) => `${hours} ساعة`,
  minutesClockNumberText: (minutes) => `${minutes} دقيقة`,
  secondsClockNumberText: (seconds) => `${seconds} ثانية`,
  selectViewText: (view) => `اختر ${AR_VIEWS[view]}`,
  calendarWeekNumberHeaderLabel: 'رقم الأسبوع',
  calendarWeekNumberHeaderText: '#',
  calendarWeekNumberAriaLabelText: (weekNumber) => `الأسبوع ${weekNumber}`,
  calendarWeekNumberText: (weekNumber) => `${weekNumber}`,
  openDatePickerDialogue: (value, utils, formattedDate) =>
    formattedDate || (value !== null && utils.isValid(value))
      ? `اختر التاريخ، التاريخ المحدد هو ${formattedDate ?? utils.format(value as Date, 'fullDate')}`
      : 'اختر التاريخ',
  openTimePickerDialogue: (_value, _utils, formattedTime) =>
    formattedTime ? `اختر الوقت، الوقت المحدد هو ${formattedTime}` : 'اختر الوقت',
  fieldClearLabel: 'مسح القيمة',
  timeTableLabel: 'اختر الوقت',
  dateTableLabel: 'اختر التاريخ',
  fieldYearPlaceholder: () => 'سنة',
  fieldMonthPlaceholder: () => 'شهر',
  fieldDayPlaceholder: () => 'يوم',
  fieldWeekDayPlaceholder: () => 'يوم الأسبوع',
  fieldHoursPlaceholder: () => 'سا',
  fieldMinutesPlaceholder: () => 'دق',
  fieldSecondsPlaceholder: () => 'ثا',
  fieldMeridiemPlaceholder: () => 'ص/م',
  year: 'السنة',
  month: 'الشهر',
  day: 'اليوم',
  weekDay: 'يوم الأسبوع',
  hours: 'الساعات',
  minutes: 'الدقائق',
  seconds: 'الثواني',
  meridiem: 'صباحًا/مساءً',
  empty: 'فارغ',
};

export interface MuiLocalization {
  /** Built-in texts of @mui/material components, merged into the theme. */
  core: Localization;
  /** Texts of the date pickers (buttons, toolbar, field placeholders). */
  pickersText: PickersText;
  /** Month/day names, first day of the week and input format of the pickers. */
  dateFns: Locale;
}

const LOCALIZATIONS: Record<LanguageCode, MuiLocalization> = {
  fr: {
    core: frFR,
    pickersText: pickersFrFR.components.MuiLocalizationProvider.defaultProps.localeText,
    dateFns: fr,
  },
  en: {
    core: enUS,
    pickersText: pickersEnUS.components.MuiLocalizationProvider.defaultProps.localeText,
    // month/day/year, like the dates displayed in English (see utils/dateFormat.ts)
    dateFns: dateFnsEnUS,
  },
  ar: {
    core: arSD,
    pickersText: arPickersText,
    dateFns: ar,
  },
};

export function getMuiLocalization(language: LanguageCode): MuiLocalization {
  return LOCALIZATIONS[language];
}
